import { VoxelAsset, VoxelData } from '../voxelTypes';
import { PALETTE, getRandomizedColor } from '../voxelColor';

export interface CottageOptions {
  type: 'hobbit_hole' | 'tudor_cottage' | 'stone_mill' | 'cozy_cabin';
  roofColor?: number;
  wallColor?: number;
}

export function generateVoxelCottage(
  id: string,
  name: string,
  options: CottageOptions
): VoxelAsset {
  const voxels: VoxelData[] = [];
  const { type } = options;

  if (type === 'hobbit_hole') {
    // -------------------------------------------------------------
    // Hobbit Hole (Bag End Shire Hillside Dwelling)
    // -------------------------------------------------------------
    const radiusX = 7;
    const radiusZ = 6;
    const height = 6;
    const frontZ = 2;

    // Dome-shaped earthen hill with lush grass and wildflower roof
    for (let x = -radiusX; x <= radiusX; x++) {
      for (let z = -radiusZ; z <= radiusZ; z++) {
        const normDistSq = (x * x) / (radiusX * radiusX) + (z * z) / (radiusZ * radiusZ);
        if (normDistSq <= 1.0) {
          const maxH = Math.floor(height * Math.sqrt(1.0 - normDistSq));
          for (let y = 0; y <= maxH; y++) {
            if (y === maxH && y >= 1) {
              const flowerRoll = Math.random();
              let color = PALETTE.GRASS_LUSH;
              if (flowerRoll > 0.94) color = PALETTE.FLOWER_YELLOW;
              else if (flowerRoll > 0.88) color = PALETTE.FLOWER_RED;
              else if (flowerRoll > 0.84) color = PALETTE.FLOWER_PURPLE;
              else color = getRandomizedColor(PALETTE.GRASS_LUSH, 0.02);

              voxels.push({ x, y, z, color, materialType: 'foliage' });
            } else {
              voxels.push({
                x, y, z,
                color: getRandomizedColor(PALETTE.DIRT_DARK, 0.02),
                materialType: 'opaque'
              });
            }
          }
        }
      }
    }

    // Carve out front entrance bay
    for (let x = -4; x <= 4; x++) {
      for (let y = 0; y <= 5; y++) {
        for (let z = frontZ; z <= radiusZ; z++) {
          const idx = voxels.findIndex(v => v.x === x && v.y === y && v.z === z);
          if (idx !== -1) voxels.splice(idx, 1);
        }
      }
    }

    // Cobblestone Facade Wall with mossy micro-accents
    for (let x = -4; x <= 4; x += 0.5) {
      for (let y = 0; y <= 4.2; y += 0.5) {
        if (Math.hypot(x, y - 1.8) <= 4.2) {
          const isMoss = Math.random() < 0.15;
          voxels.push({
            x, y, z: frontZ,
            color: isMoss ? PALETTE.LEAF_OAK_LUSH : ((Math.floor(x * 2) + Math.floor(y * 2)) % 2 === 0 ? PALETTE.COBBLE : PALETTE.STONE_MEDIUM),
            materialType: 'opaque',
            scale: 0.5
          });
        }
      }
    }

    // High-Detail Micro-Voxel Round Hobbit Door (Center at x = 0, y = 2.0, z = frontZ + 0.3)
    const doorRadius = 1.4;
    const doorCenterY = 2.0;
    const doorZ = frontZ + 0.3;

    // 1. Dark Oak Outer Rim Frame
    for (let angle = 0; angle < 360; angle += 15) {
      const rad = (angle * Math.PI) / 180;
      const rx = Math.cos(rad) * (doorRadius + 0.15);
      const ry = Math.sin(rad) * (doorRadius + 0.15) + doorCenterY;
      voxels.push({
        x: rx, y: ry, z: doorZ,
        color: PALETTE.WOOD_OAK_DARK,
        materialType: 'opaque',
        scale: 0.4
      });
    }

    // 2. Vertical Wood Planks with Center Brass Handle
    for (let dx = -doorRadius; dx <= doorRadius; dx += 0.3) {
      for (let dy = -doorRadius; dy <= doorRadius; dy += 0.3) {
        const dist = Math.hypot(dx, dy);
        if (dist <= doorRadius) {
          const py = doorCenterY + dy;
          const px = dx;
          const isSeam = Math.abs(dx % 0.6) < 0.1;
          const isHandle = dist < 0.25;

          let color = isSeam ? PALETTE.WOOD_OAK_DARK : PALETTE.WOOD_OAK_MEDIUM;
          if (isHandle) color = PALETTE.LAMP_GOLD;

          voxels.push({
            x: px, y: py, z: doorZ + 0.05,
            color,
            materialType: isHandle ? 'emissive' : 'opaque',
            scale: 0.32
          });
        }
      }
    }

    // 3. Stone Arch Keystone Frame surrounding the round door
    for (let angle = 0; angle <= 180; angle += 15) {
      const rad = (angle * Math.PI) / 180;
      const ax = Math.cos(rad) * (doorRadius + 0.4);
      const ay = Math.sin(rad) * (doorRadius + 0.4) + doorCenterY;
      voxels.push({
        x: ax, y: ay, z: doorZ,
        color: PALETTE.STONE_LIGHT,
        materialType: 'opaque',
        scale: 0.5
      });
    }

    // High-Detail Circular Porthole Windows with Cross Lattices (at x = -3.2 and x = 3.2)
    const windowPositions = [{ x: -3.2, y: 2.1 }, { x: 3.2, y: 2.1 }];
    windowPositions.forEach(pos => {
      const winR = 0.8;
      // Glass Panes
      for (let wx = -winR; wx <= winR; wx += 0.3) {
        for (let wy = -winR; wy <= winR; wy += 0.3) {
          if (Math.hypot(wx, wy) <= winR) {
            const isCrossMullion = Math.abs(wx) < 0.12 || Math.abs(wy) < 0.12;
            voxels.push({
              x: pos.x + wx, y: pos.y + wy, z: frontZ + 0.2,
              color: isCrossMullion ? PALETTE.WOOD_OAK_DARK : PALETTE.GLASS_GLOW,
              materialType: isCrossMullion ? 'opaque' : 'emissive',
              scale: 0.32
            });
          }
        }
      }

      // Flower Boxes with micro-roses under windows
      for (let fbx = -0.8; fbx <= 0.8; fbx += 0.4) {
        voxels.push({
          x: pos.x + fbx, y: pos.y - 1.0, z: frontZ + 0.4,
          color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.4
        });
        voxels.push({
          x: pos.x + fbx, y: pos.y - 0.7, z: frontZ + 0.4,
          color: fbx % 0.8 === 0 ? PALETTE.FLOWER_RED : PALETTE.FLOWER_YELLOW, materialType: 'foliage', scale: 0.35
        });
      }
    });

    // Sconce Doorway Lanterns
    voxels.push({ x: -2.0, y: 3.0, z: frontZ + 0.5, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: 0.5 });
    voxels.push({ x: 2.0, y: 3.0, z: frontZ + 0.5, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: 0.5 });

    // Flagstone Front Path & Shire Mailbox
    for (let fz = frontZ + 0.8; fz <= frontZ + 4.5; fz += 0.6) {
      for (let fx = -1.2; fx <= 1.2; fx += 0.6) {
        const isCenter = Math.abs(fx) < 0.8;
        voxels.push({
          x: fx, y: 0.1, z: fz,
          color: isCenter ? PALETTE.PATH_SAND : PALETTE.COBBLE,
          materialType: 'opaque',
          scaleX: 0.55, scaleZ: 0.55, scaleY: 0.2
        });
      }
    }

    // Small Wooden Shire Mailbox at path entrance
    const mbX = 1.6, mbZ = frontZ + 4.0;
    voxels.push({ x: mbX, y: 0.5, z: mbZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 0.2, scaleZ: 0.2, scaleY: 0.8 });
    voxels.push({ x: mbX, y: 1.1, z: mbZ, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scale: 0.5 });
    voxels.push({ x: mbX, y: 1.1, z: mbZ + 0.2, color: PALETTE.FLOWER_RED, materialType: 'opaque', scale: 0.25 }); // Mail flag

    // Cobblestone Chimney on hilltop
    const chimX = 2, chimZ = -1;
    for (let cy = 3; cy <= 7; cy++) {
      voxels.push({
        x: chimX, y: cy, z: chimZ,
        color: cy === 7 ? PALETTE.ROOF_RED_CLAY : getRandomizedColor(PALETTE.COBBLE, 0.03),
        materialType: 'opaque'
      });
    }

  } else if (type === 'tudor_cottage' || type === 'cozy_cabin') {
    // -------------------------------------------------------------
    // Tudor / Cozy Cabin Cottage (High-Detail Micro-Voxel Architecture)
    // -------------------------------------------------------------
    const width = 8;
    const depth = 10;
    const wallHeight = 5;

    const isStone = type === 'tudor_cottage';
    const roofColor = options.roofColor || (isStone ? PALETTE.SLATE_ROOF : PALETTE.THATCH_YELLOW);
    const wallColor = options.wallColor || (isStone ? PALETTE.STONE_MEDIUM : PALETTE.WOOD_OAK_MEDIUM);

    // 1. Cobblestone Foundation with Ivy & Moss Accents (y = 0), and the boards inside it.
    //
    // The foundation used to be a bare ring: correct from the street, where the only thing you can
    // see of y = 0 is its outer face, and wrong the moment the front wall comes off — the house had
    // no floor, and you looked straight down at the grass of the parcel through it.
    for (let x = -width / 2; x <= width / 2; x++) {
      for (let z = -depth / 2; z <= depth / 2; z++) {
        const isEdge = Math.abs(x) === Math.floor(width / 2) || Math.abs(z) === Math.floor(depth / 2);
        if (isEdge) {
          const isIvy = Math.random() < 0.22;
          voxels.push({
            x, y: 0, z,
            color: isIvy ? PALETTE.LEAF_OAK_LUSH : getRandomizedColor(PALETTE.STONE_MEDIUM, 0.03),
            materialType: isIvy ? 'foliage' : 'opaque'
          });
        } else {
          // Floorboards, laid along the length of the room and alternating in tone so the floor
          // reads as boards rather than as one flat slab of colour.
          const plank = (Math.abs(Math.round(x)) % 2 === 0)
            ? PALETTE.WOOD_OAK_MEDIUM
            : PALETTE.WOOD_OAK_DARK;
          voxels.push({ x, y: 0, z, color: getRandomizedColor(plank, 0.02), materialType: 'opaque' });
        }
      }
    }

    // 2. Walls with Half-Timbering (Tudor) or Stacked Round Logs (Cabin)
    for (let x = -width / 2; x <= width / 2; x++) {
      for (let z = -depth / 2; z <= depth / 2; z++) {
        for (let y = 1; y < wallHeight; y++) {
          const isEdgeX = Math.abs(x) === Math.floor(width / 2);
          const isEdgeZ = Math.abs(z) === Math.floor(depth / 2);

          if (isEdgeX || isEdgeZ) {
            const isCorner = isEdgeX && isEdgeZ;
            const isTimber = isCorner || y === 1 || y === wallHeight - 1 || (x % 3 === 0) || (z % 3 === 0);

            // Front Door (z == depth/2, x near center)
            if (z === Math.floor(depth / 2) && (x === 0 || x === -1) && y <= 3) {
              const isHandle = x === 0 && y === 2;
              const color = isHandle ? PALETTE.LAMP_GOLD : (y === 3 ? PALETTE.WOOD_OAK_DARK : PALETTE.WOOD_OAK_MEDIUM);
              voxels.push({
                x, y, z: z + 0.1,
                color,
                materialType: isHandle ? 'emissive' : 'opaque',
                scale: isHandle ? 0.35 : 1
              });
            }
            // Doorway Sconce Lanterns
            else if (z === Math.floor(depth / 2) && (x === -2 || x === 1) && y === 3) {
              voxels.push({ x, y, z: z + 0.4, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: 0.5 });
            }
            // Latticed Windows on Front & Sides with Micro Flower Boxes & Wooden Shutters
            else if ((y === 2 || y === 3) && (
              (z === Math.floor(depth / 2) && (x === -3 || x === 2)) ||
              (isEdgeX && (z === -2 || z === 2))
            )) {
              // Real Glass Window
              voxels.push({ x, y, z, color: PALETTE.GLASS_WINDOW, materialType: 'glass' });
              
              // Inner light for warm glow through the glass
              const innerX = isEdgeX ? (x > 0 ? x - 1 : x + 1) : x;
              const innerZ = (z === Math.floor(depth / 2)) ? z - 1 : z;
              // Only add one light voxel behind to avoid overlapping duplicates
              if (y === 2) {
                voxels.push({ x: innerX, y, z: innerZ, color: PALETTE.LAMP_GOLD, materialType: 'emissive' });
              }

              // Window Shutters on both sides
              if (z === Math.floor(depth / 2)) {
                const shutterX = x < 0 ? x - 0.6 : x + 0.6;
                voxels.push({ x: shutterX, y, z: z + 0.15, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 0.35, scaleY: 0.8, scaleZ: 0.1 });
              }

              // Micro Flower box under front window on y === 2
              if (y === 2 && z === Math.floor(depth / 2)) {
                voxels.push({ x, y: 1.2, z: z + 0.4, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.4 });
                voxels.push({ x, y: 1.5, z: z + 0.4, color: Math.abs(x) % 2 === 0 ? PALETTE.FLOWER_RED : PALETTE.FLOWER_YELLOW, materialType: 'foliage', scale: 0.35 });
              }
            }
            // Wall Generation
            else if (!isStone) {
              // Log Cabin Walls
              const isCorner = isEdgeX && isEdgeZ;
              const isDarkLog = (y % 2 === 0);
              const logColor = isDarkLog ? PALETTE.WOOD_OAK_DARK : wallColor;
              voxels.push({
                x, y, z,
                color: isCorner ? (isDarkLog ? PALETTE.WOOD_OAK_MEDIUM : PALETTE.WOOD_OAK_DARK) : getRandomizedColor(logColor, 0.02),
                materialType: 'opaque',
                scaleX: isCorner ? 1.1 : (isEdgeZ ? 1 : 0.85),
                scaleZ: isCorner ? 1.1 : (isEdgeX ? 1 : 0.85),
                scaleY: 0.85
              });
            } else {
              // Stone Cottage Walls
              const isCorner = isEdgeX && isEdgeZ;
              voxels.push({ 
                x, y, z, 
                color: getRandomizedColor(isCorner ? PALETTE.STONE_DARK : PALETTE.STONE_MEDIUM, 0.04), 
                materialType: 'opaque' 
              });
            }
          }
        }
      }
    }

    // Porch, Steps & Stacked Firewood Rack
    const frontZ = Math.floor(depth / 2);
    // Flagstone Steps
    for (let px = -2; px <= 1; px += 0.5) {
      voxels.push({ x: px, y: 0.2, z: frontZ + 1.0, color: PALETTE.STONE_MEDIUM, materialType: 'opaque', scaleX: 0.5, scaleZ: 0.5, scaleY: 0.2 });
    }
    // Porch Bench & Posts for Cozy Cabin
    if (!isStone) {
      voxels.push({ x: 2.2, y: 1.2, z: frontZ + 0.8, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scale: 0.45 });
      voxels.push({ x: 2.7, y: 1.2, z: frontZ + 0.8, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque', scale: 0.45 });
    }

    // Firewood Rack on side wall (x = width/2 + 0.5)
    const sideX = Math.floor(width / 2) + 0.5;
    for (let fz = -2; fz <= 1; fz += 0.6) {
      for (let fy = 0.4; fy <= 1.6; fy += 0.6) {
        voxels.push({
          x: sideX, y: fy, z: fz,
          color: (fz + fy) % 2 === 0 ? PALETTE.WOOD_OAK_MEDIUM : PALETTE.WOOD_OAK_DARK,
          materialType: 'opaque',
          scale: 0.45
        });
      }
    }

    // 3. Solid Interior Ceiling
    for (let x = -Math.floor(width / 2); x <= Math.floor(width / 2); x++) {
      for (let z = -Math.floor(depth / 2); z <= Math.floor(depth / 2); z++) {
        voxels.push({
          x, y: wallHeight - 1, z,
          color: PALETTE.WOOD_OAK_DARK,
          materialType: 'opaque'
        });
      }
    }

    // 4. Solid Pitched Roof with Decorative Eave Trim
    const overhang = 1;
    let roofW = width / 2 + overhang;
    let ry = wallHeight;

    while (roofW >= 0) {
      const rxMax = Math.floor(roofW);
      for (let x = -rxMax; x <= rxMax; x++) {
        for (let z = -(Math.floor(depth / 2) + overhang); z <= (Math.floor(depth / 2) + overhang); z++) {
          const isOuterRoof = Math.abs(x) === rxMax || roofW <= 0.5;
          const isGableWall = Math.abs(z) >= Math.floor(depth / 2);

          if (isOuterRoof) {
            voxels.push({
              x, y: ry, z,
              color: getRandomizedColor(roofColor, 0.02),
              materialType: 'opaque'
            });
          } else if (isGableWall) {
            voxels.push({
              x, y: ry, z,
              color: isStone ? PALETTE.STONE_MEDIUM : PALETTE.WOOD_OAK_MEDIUM,
              materialType: 'opaque'
            });
          } else {
            voxels.push({
              x, y: ry, z,
              color: PALETTE.WOOD_OAK_DARK,
              materialType: 'opaque'
            });
          }
        }
      }
      roofW -= 0.8;
      ry++;
    }

    // Cute Roof Dormer Window (front slope at x = -1, z = depth/2 - 1, y = wallHeight + 1)
    const dormerY = wallHeight + 1;
    const dormerZ = Math.floor(depth / 2) - 1;
    voxels.push({ x: -1, y: dormerY, z: dormerZ + 0.5, color: PALETTE.GLASS_GLOW, materialType: 'emissive', scale: 0.6 });
    voxels.push({ x: -1, y: dormerY + 0.6, z: dormerZ + 0.5, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 0.7, scaleY: 0.3, scaleZ: 0.7 });

    // 5. Cobblestone Chimney with Terra Cotta Pot Cap
    const chimX = Math.floor(width / 2) - 1;
    const chimZ = -Math.floor(depth / 2) + 2;
    for (let cy = 0; cy <= ry + 1; cy++) {
      const isTopCap = cy === ry + 1;
      voxels.push({
        x: chimX, y: cy, z: chimZ,
        color: isTopCap ? PALETTE.ROOF_RED_CLAY : (cy >= ry ? PALETTE.STONE_DARK : getRandomizedColor(PALETTE.STONE_MEDIUM, 0.03)),
        materialType: 'opaque'
      });
    }

  } else {
    // -------------------------------------------------------------
    // Stone Watermill (Detailed Mossy Stone & Water Chute)
    // -------------------------------------------------------------
    const size = 8;
    const height = 6;

    for (let x = -size / 2; x <= size / 2; x++) {
      for (let z = -size / 2; z <= size / 2; z++) {
        for (let y = 0; y < height; y++) {
          const isEdgeX = Math.abs(x) === Math.floor(size / 2);
          const isEdgeZ = Math.abs(z) === Math.floor(size / 2);
          if (isEdgeX || isEdgeZ) {
            // Main entrance door facing the bridge/path approach (front side)
            if (z === Math.floor(size / 2) && (x >= -1 && x <= 1) && y <= 3) {
              continue;
            }
            const isMoss = Math.sin(x * 3 + y * 2) > 0.7;
            if ((y === 3 || y === 4) && (x % 3 === 0 || z % 3 === 0)) {
              voxels.push({ x, y, z, color: PALETTE.GLASS_GLOW, materialType: 'emissive' });
            } else {
              voxels.push({
                x, y, z,
                color: isMoss ? PALETTE.LEAF_OAK_LUSH : getRandomizedColor(PALETTE.STONE_MEDIUM, 0.03),
                materialType: 'opaque'
              });
            }
          }
        }
      }
    }

    // Main Entrance Timber Doors (double doors, swung open inwards against left and right interior walls)
    for (let dy = 0; dy <= 3; dy++) {
      // Left open door panel
      voxels.push({
        x: -1.2,
        y: dy,
        z: Math.floor(size / 2) - 1, // z = 3 (recessed inside)
        color: PALETTE.WOOD_OAK_DARK,
        materialType: 'opaque',
        scaleX: 0.15,
        scaleZ: 1.2,
        scaleY: 1.0
      });
      // Right open door panel
      voxels.push({
        x: 1.2,
        y: dy,
        z: Math.floor(size / 2) - 1, // z = 3 (recessed inside)
        color: PALETTE.WOOD_OAK_DARK,
        materialType: 'opaque',
        scaleX: 0.15,
        scaleZ: 1.2,
        scaleY: 1.0
      });
    }

    // Solid Ceiling & Slate Roof
    for (let x = -size / 2; x <= size / 2; x++) {
      for (let z = -size / 2; z <= size / 2; z++) {
        voxels.push({ x, y: height - 1, z, color: PALETTE.STONE_DARK, materialType: 'opaque' });
      }
    }

    for (let y = height; y <= height + 4; y++) {
      const r = (height + 4 - y) * 0.9;
      for (let x = -Math.ceil(r); x <= Math.ceil(r); x++) {
        for (let z = -Math.ceil(r); z <= Math.ceil(r); z++) {
          if (Math.hypot(x, z) <= r) {
            voxels.push({
              x, y, z,
              color: getRandomizedColor(PALETTE.SLATE_ROOF, 0.02),
              materialType: 'opaque'
            });
          }
        }
      }
    }

    // Wooden Water Sluice / Chute pouring water over wheel axle (x = 4 to 6, y = 4)
    for (let cx = Math.floor(size / 2); cx <= Math.floor(size / 2) + 2; cx += 0.5) {
      voxels.push({ x: cx, y: 4.1, z: 0, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scaleX: 0.5, scaleZ: 0.8, scaleY: 0.2 });
      // Water flow inside chute
      voxels.push({ x: cx, y: 4.3, z: 0, color: PALETTE.WATER_CYAN, materialType: 'water', scaleX: 0.5, scaleZ: 0.6, scaleY: 0.15 });
    }

    // Waterwheel Mounting Hub Bracket
    const wheelX = Math.floor(size / 2) + 1;
    const wheelCenterY = 2;
    const wheelCenterZ = 0;

    for (let x = Math.floor(size / 2); x <= wheelX + 1; x++) {
      voxels.push({ x, y: wheelCenterY, z: wheelCenterZ, color: PALETTE.STONE_DARK, materialType: 'opaque' });
    }
  }

  return {
    id,
    name,
    category: 'cottage',
    description: `Micro-voxel ${type.replace('_', ' ')}`,
    voxels,
    dimensions: { x: 12, y: 10, z: 12 }
  };
}

export function generateWatermillWheelVoxels(): VoxelData[] {
  const voxels: VoxelData[] = [];
  const wheelRadius = 3.6;

  // Central Iron Axle Shaft & Brass Collar
  for (let ax = -0.5; ax <= 1.5; ax += 0.5) {
    voxels.push({ x: ax, y: 0, z: 0, color: PALETTE.STONE_DARK, materialType: 'opaque', scale: 0.45 });
  }
  // Decorative Brass Ring Collar surrounding axle hub at x = 0.5 (strictly non-overlapping)
  [0.35, -0.35].forEach(offset => {
    voxels.push({ x: 0.5, y: offset, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'opaque', scale: 0.25 });
    voxels.push({ x: 0.5, y: 0, z: offset, color: PALETTE.LAMP_GOLD, materialType: 'opaque', scale: 0.25 });
  });

  // 8 Spokes, Dual Outer Rings, and Curved Bucket Paddles
  for (let angleDeg = 0; angleDeg < 360; angleDeg += 45) {
    const rad = (angleDeg * Math.PI) / 180;
    const cosR = Math.cos(rad);
    const sinR = Math.sin(rad);

    // Spokes from center out
    for (let r = 0.6; r <= wheelRadius; r += 0.6) {
      const sy = sinR * r;
      const sz = cosR * r;
      voxels.push({
        x: 0, y: sy, z: sz,
        color: PALETTE.WOOD_OAK_DARK,
        materialType: 'opaque',
        scale: 0.4
      });
      voxels.push({
        x: 1.0, y: sy, z: sz,
        color: PALETTE.WOOD_OAK_DARK,
        materialType: 'opaque',
        scale: 0.4
      });
    }

    // Outer Rim Ring segment
    const rimY = sinR * wheelRadius;
    const rimZ = cosR * wheelRadius;
    voxels.push({ x: 0, y: rimY, z: rimZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.5 });
    voxels.push({ x: 1.0, y: rimY, z: rimZ, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque', scale: 0.5 });

    // Wide Bucket Paddle Scoop (single solid stretched voxel to prevent any moire or overlapping artifacts)
    const paddleY = sinR * (wheelRadius + 0.35);
    const paddleZ = cosR * (wheelRadius + 0.35);

    voxels.push({
      x: 0.5,
      y: paddleY,
      z: paddleZ,
      color: PALETTE.WOOD_OAK_DARK,
      materialType: 'opaque',
      scaleX: 1.4,
      scaleY: 0.45,
      scaleZ: 0.45
    });
  }

  return voxels;
}
